import { Anchor, Box, Button as MantineButton, Flex, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import type { DeepPartial } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-scroll';

import { DevicePreviewModal } from '../../components/global/DevicePreviewModal/DevicePreviewModal.tsx';
import { EventFormLayout } from '../../components/layout/EventFormLayout.tsx';
import { Button } from '../../components/ui/Button/Button.tsx';
import { useEventResubmitMutation } from '../../data/events/useEventResubmitMutation.ts';
import { useEventsItemQuery } from '../../data/events/useEventsItemQuery.ts';
import { useEventsItemUpdateMutation } from '../../data/events/useEventsItemUpdateMutation.ts';
import { env } from '../../env.ts';
import { EventForm } from '../../forms/event/EventForm/EventForm.tsx';
import type { EventFormComplete } from '../../forms/event/EventForm/schema.ts';
import { useI18n } from '../../hooks/useI18n.ts';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges.ts';
import { EventPreview } from '../incompleteEventEdit/EventPreview.tsx';
import { UnsavedChangesModal } from '../incompleteEventEdit/UnsavedChangesModal.tsx';
import { DeclineNotice } from './DeclineNotice.tsx';
import { eventApiToEventEditForm } from './lib/eventApiToEventEditForm.ts';
import { eventEditFormToEventApi } from './lib/eventEditFormToEventApi.ts';
import { useRemoveAllCurrentTargetAudiencesOfEventMutation } from './lib/useRemoveAllCurrentTargetAudiencesOfEventMutation.ts';

export const EventEdit: FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const hiddenSubmitButtonRef = useRef<HTMLButtonElement>(null);
  const [devicePreviewOpen, setDevicePreviewOpen] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        label: t('event-form_section_title--about-event') ?? 'About event',
        to: 'about-event',
      },
      {
        label: t('event-form_section_title--when-and-where') ?? 'When and where?',
        to: 'where-and-when',
      },
      {
        label: t('event-form_section_title--artwork') ?? 'Artwork',
        to: 'cover-image',
      },
      {
        label: t('event-form_section_title--links') ?? 'Links',
        to: 'links',
      },
    ],
    [t],
  );

  const { id } = useParams<{ id: string }>();

  const { mutate: mutateRemoveAllTargetAudiences, isPending: mutateRemoveAllTargetAudiencesIsPending } =
    useRemoveAllCurrentTargetAudiencesOfEventMutation();

  const { data: eventData } = useEventsItemQuery(id);
  const { mutate: mutateEvent, isPending: mutateEventIsPending } = useEventsItemUpdateMutation(id);
  const { mutate: mutateResubmit, isPending: mutateResubmitIsPending } = useEventResubmitMutation();

  const isDeclined = eventData?.status === 'declined';
  // Which button submitted the form: the form only reports that it is valid, not who asked.
  const submitIntentRef = useRef<'save' | 'resubmit'>('save');

  const [currentFormValues, setCurrentFormValues] = useState<DeepPartial<EventFormComplete>>(
    eventApiToEventEditForm(eventData),
  );

  const [defaultFormValues, setDefaultFormValues] = useState<DeepPartial<EventFormComplete> | null>(null);

  const navigateToDashboard = useCallback(() => {
    // The tab the event is listed on, or the promoter lands on a list without it.
    const slug =
      eventData?.status === 'published' ? 'published' : eventData?.status === 'declined' ? 'declined' : 'pending';

    void navigate(`/dashboard/${slug}`);
  }, [eventData?.status, navigate]);

  useEffect(() => {
    if (!eventData) {
      setDefaultFormValues(null);
      return;
    }

    const formData = eventApiToEventEditForm(eventData);

    setCurrentFormValues(formData);
    setDefaultFormValues(formData);
  }, [eventData]);

  const handleClick = () => {
    submitIntentRef.current = 'save';
    hiddenSubmitButtonRef?.current?.click();
  };

  const handleResubmitClick = () => {
    submitIntentRef.current = 'resubmit';
    hiddenSubmitButtonRef?.current?.click();
  };

  const resubmit = useCallback(() => {
    if (!id) return;

    mutateResubmit(id, {
      onSuccess: () => {
        notifications.show({
          title: t('notifications_title--event-resubmitted') ?? 'Event Resubmitted',
          color: 'green',
          message: t('notifications_message--event-resubmitted') ?? 'Your event has been sent for review again.',
        });

        void navigate('/dashboard/pending');
      },
      onError: () => {
        // The changes are saved at this point; only the status change failed.
        notifications.show({
          title: t('notifications_title--event-save-error') ?? 'Error Saving',
          message:
            t('notifications_message--event-resubmit-error') ??
            'Your changes were saved, but the event could not be resubmitted. Please try again.',
          color: 'red',
        });
      },
    });
  }, [id, mutateResubmit, navigate, t]);

  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const handleSaveEvent = useCallback(
    (callbacks?: { onSuccess?: () => void }, intent: 'save' | 'resubmit' = 'save') => {
      const eventApiUpdateData = eventEditFormToEventApi(currentFormValues);

      mutateRemoveAllTargetAudiences(id, {
        onSuccess: () => {
          mutateEvent(eventApiUpdateData, {
            onSuccess: () => {
              if (intent === 'resubmit') {
                resubmit();
                return;
              }

              const message =
                currentFormValues.name && currentFormValues.name !== ''
                  ? (t('notifications_message--event-saved-with-name', {
                      name: currentFormValues.name,
                    }) ?? `The event "${currentFormValues.name}" has been saved successfully.`)
                  : (t('notifications_message--event-saved') ?? 'Your event has been saved successfully.');

              notifications.show({
                title: t('notifications_title--event-saved') ?? 'Event Saved Successfully',
                color: 'green',
                message,
              });

              callbacks?.onSuccess?.();

              navigateToDashboard();
            },
            onError: () => {
              notifications.show({
                title: t('notifications_title--event-save-error') ?? 'Error Saving',
                message:
                  t('notifications_message--event-save-error') ??
                  'Unable to save your event because of technical problems.',
                color: 'red',
              });
            },
          });
        },
      });
    },
    [currentFormValues, mutateRemoveAllTargetAudiences, id, mutateEvent, resubmit, t, navigateToDashboard],
  );

  const preview = useMemo(() => {
    if (!currentFormValues) {
      return null;
    }

    return (
      <Stack gap="xs" pos="sticky" top={24} w="auto">
        <Flex align="flex-end" gap="xs" h={195 - 24} justify="flex-end" w="100%" wrap="wrap">
          <Button
            intent={isDeclined ? 'secondary' : 'primary'}
            loading={(mutateEventIsPending || mutateRemoveAllTargetAudiencesIsPending) && !mutateResubmitIsPending}
            text={t('button--save-changes') ?? 'Save changes'}
            type="button"
            onClick={handleClick}
          />
          {isDeclined ? (
            <Button
              intent="primary"
              loading={mutateResubmitIsPending}
              text={t('button--resubmit-for-review') ?? 'Resubmit for review'}
              type="button"
              onClick={handleResubmitClick}
            />
          ) : null}
        </Flex>
        <EventPreview
          eventPreviewData={{
            ...currentFormValues,
            afterImageContent: env.VITE_FRONTEND_URL ? (
              <Box mb={4} mt={8}>
                {eventData?.status === 'published' && eventData?.slug ? (
                  // Published events have a real URL — link to the live page.
                  <Anchor
                    component="a"
                    href={`${env.VITE_FRONTEND_URL}/events/${eventData.slug}/`}
                    rel="noopener noreferrer"
                    target="_blank"
                    underline="never"
                  >
                    <MantineButton color="dark" size="xs" variant="outline">
                      {t('event-form_button--view-on-gogigi') ?? 'View on gogigi.com'}
                    </MantineButton>
                  </Anchor>
                ) : (
                  // Pending / created / declined — not yet public, so show the
                  // iframe preview modal instead.
                  <MantineButton color="dark" size="xs" variant="outline" onClick={() => setDevicePreviewOpen(true)}>
                    {t('event-form_button--show-on-device') ?? 'Show on device'}
                  </MantineButton>
                )}
              </Box>
            ) : undefined,
          }}
        />
      </Stack>
    );
  }, [
    currentFormValues,
    eventData?.status,
    eventData?.slug,
    isDeclined,
    mutateEventIsPending,
    mutateRemoveAllTargetAudiencesIsPending,
    mutateResubmitIsPending,
    t,
  ]);

  const eventForm = useMemo(() => {
    if (!defaultFormValues) {
      return null;
    }

    return (
      <EventForm
        defaultValues={defaultFormValues}
        submitButton={<button hidden ref={hiddenSubmitButtonRef} type="submit" />}
        onCurrentFormValueChange={setCurrentFormValues}
        onSubmit={() => handleSaveEvent(undefined, submitIntentRef.current)}
      />
    );
  }, [defaultFormValues, handleSaveEvent]);

  const hasUnsavedChanges = useUnsavedChanges(defaultFormValues, currentFormValues);

  const handleBackToDashboard = () => {
    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    navigateToDashboard();
  };

  const handleUnsavedChangesClose = () => setIsUnsavedChangesModalOpen(false);

  const handleSaveChangesAndNavigate = () => {
    handleSaveEvent({ onSuccess: () => navigateToDashboard() });
  };

  const handleDiscardChangesAndNavigate = () => navigateToDashboard();

  return (
    <>
      <UnsavedChangesModal
        opened={isUnsavedChangesModalOpen}
        saveChangesIsPending={mutateEventIsPending}
        onClose={handleUnsavedChangesClose}
        onDontSaveChanges={handleDiscardChangesAndNavigate}
        onSaveChanges={handleSaveChangesAndNavigate}
      />
      <DevicePreviewModal
        formData={currentFormValues}
        opened={devicePreviewOpen}
        onClose={() => setDevicePreviewOpen(false)}
      />

      <EventFormLayout
        backButton={{
          text: t('button--back-to-dashboard') ?? 'Go back to Dashboard',
          onClick: handleBackToDashboard,
        }}
        centralContent={
          isDeclined ? (
            <Stack gap="lg">
              <DeclineNotice declineHistory={eventData?.decline_history} declineReason={eventData?.decline_reason} />
              {eventForm}
            </Stack>
          ) : (
            eventForm
          )
        }
        rightContent={preview}
        sectionSubtitle={t('title--edit-event') ?? 'Edit event'}
        sectionTitle={currentFormValues.name ?? 'Event'}
        sideMenuContent={
          <Stack align="flex-start" gap="xxs" pos="sticky" top={110 + 40}>
            {menuItems.map((menuItem, i) => (
              <Anchor
                hashSpy
                smooth
                spy
                activeStyle={{ color: 'purple', textDecoration: 'underline' }}
                c="gray.8"
                component={Link}
                fw="600"
                key={menuItem.to}
                offset={-100 - i * 33}
                size="sm"
                to={menuItem.to}
                underline="hover"
              >
                {menuItem.label}
              </Anchor>
            ))}
          </Stack>
        }
      />
    </>
  );
};
